import { useCallback, useMemo } from 'react';
import moment from 'moment';

import { selectAuth } from '~store/slices/auth/index';

import { useAppSelector } from './useAppSelector';
import { useSubscription } from './useSubscription';
import { useLocalStorage } from './useLocalStorage';
import { useTypePartnerCompany } from './useTypePartnerCompany';

export interface UIV2State {
  isAvailableUIV2: boolean;
  isEnabledUIV2: boolean | undefined;
  toggleEnabledUIV2: () => void;
}

const AVAILABLE_TO_DATE = moment('2026-01-05').toDate();
const DEFAULTS_ENABLED_PLANS = ['PLUS', 'PREMIUM', 'CORPORATE', 'E-FLEX'];

export const useUIV2State = (): UIV2State => {
  const { user } = useAppSelector(selectAuth);

  const subscription = useSubscription();
  const { isGiver } = useTypePartnerCompany();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isAvailableUIV2 = useMemo(() => {
    if (isGiver) return false;

    if (!user?.company.creationDateTime) return false;

    const currentDate = moment(user.company.creationDateTime);

    return currentDate.isBefore(AVAILABLE_TO_DATE, 'day');
  }, [isGiver, user?.company.creationDateTime]);

  const currentPlanName = useMemo(
    () => subscription?.description?.replace('ANUAL', '').trimEnd(),
    [subscription?.description],
  );

  const initialIsEnabledUIV2 = useMemo(() => {
    if (isGiver) return true;

    if (currentPlanName && DEFAULTS_ENABLED_PLANS.includes(currentPlanName))
      return true;

    return false;
  }, [currentPlanName, isGiver]);

  const { state: isEnabledUIV2, set: setIsEnabledUIV2 } = useLocalStorage({
    key: 'routix-new-ui',
    initialValue: initialIsEnabledUIV2,
  });

  const toggleEnabledUIV2 = useCallback(() => {
    setIsEnabledUIV2(!isEnabledUIV2);

    window.location.reload(); // Reload Page sync changes
  }, [isEnabledUIV2, setIsEnabledUIV2]);

  return {
    // @TODO: Force to active UI V2 by default
    isAvailableUIV2: false,
    isEnabledUIV2: true,
    toggleEnabledUIV2,
  };
};
